import { DOCUMENT } from "@angular/common";
import { Component, ElementRef, Inject, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { Observable, ReplaySubject } from "rxjs";
import { JsLoaderService } from "../../../jsloader/jsloader.service";
import { Modal } from 'flowbite';
import { FormsModule } from '@angular/forms';
import { I18nServices } from "../../../i18n/i18n.service";
import { OrderService } from "../../../order/order.service";
import { Order } from "../../../domain/order";

declare var Stripe: any;

@Component({
    templateUrl: './stripecardpopup.component.html',
    selector: 'stripe-card-popup',
    styleUrls: [ './stripecardpopup.component.scss' ],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StripeCardPopupComponent {


    @Output('onChange')
    public onChange: ReplaySubject<any> = new ReplaySubject();

    protected stripe;
    protected card;
    protected clientSecret: string;

    public order: Order;

    protected stripeInitialized = false;

    public stripePublicKey = null;

    @ViewChild('stripeCardModal') protected stripeCardModal: ElementRef<HTMLElement>;
    protected modal;
    protected paymentResolver: (value: any) => void;
    protected paymentRejector: (reason?: any) => void;

    protected setupPromise: Promise<boolean>;

    // Add new property to track loading state
    protected isProcessing = false;

    public constructor(protected jsLoaderService: JsLoaderService,
                       public orderService: OrderService,
                       protected i18nService: I18nServices, 
                       @Inject(DOCUMENT) private document: Document) {

    }

    public setupStripe(clientSecret: string): Promise<boolean> {

        // Create new setup promise
        this.setupPromise = new Promise<boolean>((resolve) => {
            this.jsLoaderService.loadJavascript("https://js.stripe.com/v3/").then(() => {

                this.modal = new Modal(this.stripeCardModal.nativeElement, {
                    backdropClasses: 'bg-gray-900 bg-opacity-50 dark:bg-opacity-80 fixed inset-0',
                    onShow: () => {
                        this.document.body.classList.add('on-modal');
                    },
                    onHide: () => {
                        this.document.body.classList.remove('on-modal');
                    }
                });
    
                this.stripe = Stripe(this.stripePublicKey);
    
                this.clientSecret = clientSecret;

                var elements = this.stripe.elements(
                    {
                        locale: this.i18nService.getLanguageIsoCode(),
                        clientSecret: this.clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                fontFamily: 'Circular, "Helvetica Neue", Helvetica, Arial, sans-serif',
                            }
                        }
                    });
        
                var style = {
                    base: {
                        color: '#484848',
                        lineHeight: '24px',
                        fontFamily: 'Circular, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#888e94'
                        }
                    },
                    invalid: {
                        color: '#fa755a',
                        iconColor: '#fa755a'
                    }
                };
        
                var elementClasses = {
                    base: 'shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="username'
                };
        
                // Create an instance of the card Element.
                this.card = elements.create('card', {
                    classes: elementClasses, 
                    style: style, 
                    disableLink: true
                });
        
                // Add an instance of the card Element into the `card-element` <div>.
                this.card.mount('#card-element');
        
                // Handle real-time validation errors from the card Element.
                this.card.addEventListener('change', function(event) {
                    const displayError = document.getElementById('card-errors');
                    if (displayError) {
                        if (event.error) {
                            displayError.textContent = event.error.message;
                        } else {
                            displayError.textContent = '';
                        }
                    }
                });

                this.stripeInitialized = true;
                resolve(true);
            });
        });

        return this.setupPromise;
    }

    public displayError(error: any) {
        if(error == 'ERROR') {
            const displayError = document.getElementById('card-errors');
            alert("Atención: La tarjeta que nos ha indicado no ha sido aceptada. Por favor, pruebe con otra. Si piensa que es un error póngase en contacto con nosotros y le ayudaremos.")
            if (displayError) {
                displayError.textContent = "Tarjeta no aceptada.";
            }
        }
    }

    public getPaymentData(): Observable<any> {

        let paymentData: ReplaySubject<any> = new ReplaySubject();

        this.stripe.createToken(this.card).then((result) => {
            if (result.error) {
                // Inform the user if there was an error.
                const errorElement = document.getElementById('card-errors');
                if (errorElement) {
                    errorElement.textContent = result.error.message;
                }
                paymentData.next('ERROR');
            } else {
                // Send the token to your server.
                paymentData.next(result);
            }
        });

        return paymentData;

    }

    public clearError() {
        const displayError = document.getElementById('card-errors');
        displayError.textContent = '';
    }

    public show() {
        this.clearError();
        this.modal.show();
    }

    public hide() {
        this.modal.hide();
        if (this.paymentRejector) {
            this.paymentRejector('Payment cancelled');
        }
    }

    public confirmPayment() {
        if (this.isProcessing) return; // Prevent double submission
        
        this.isProcessing = true;
        const payButton = document.querySelector('#stripe-pay-button');
        
        if (payButton) {
            payButton.setAttribute('disabled', 'true');
            // Add loading class for spinner
            payButton.classList.add('loading');
        }
        
        this.stripe.confirmCardPayment(this.clientSecret, {
            payment_method: {
                card: this.card,
            }
        }).then((result) => {
            if (result.error) {
                // Handle error
                const errorElement = document.getElementById('card-errors');
                if (errorElement) {
                    errorElement.textContent = result.error.message;
                }
            } else {
                // Handle different payment intent statuses
                switch (result.paymentIntent.status) {
                    case 'succeeded':
                        // Payment successful
                        if (this.paymentResolver) {
                            this.paymentResolver({
                                paymentIntentId: result.paymentIntent.id,
                                paymentMethodId: result.paymentIntent.payment_method,
                                status: result.paymentIntent.status
                            });
                            this.hide();
                        }
                        break;
                        
                    case 'requires_action':
                    case 'requires_source_action':
                        // 3DS authentication is required
                        // Stripe handles the 3DS popup automatically
                        // We just need to wait for the result
                        break;
                        
                    case 'processing':
                        // Payment is processing
                        const processingElement = document.getElementById('card-errors');
                        if (processingElement) {
                            processingElement.textContent = 'Processing payment...';
                        }
                        break;
                        
                    default:
                        // Handle other statuses
                        if (this.paymentRejector) {
                            this.paymentRejector({
                                message: `Unexpected payment status: ${result.paymentIntent.status}`
                            });
                        }
                }
            }
        }).finally(() => {
            // Reset loading state
            this.isProcessing = false;
            if (payButton) {
                payButton.removeAttribute('disabled');
                payButton.classList.remove('loading');
            }
        });
    }

    public processPayment(clientSecret: string): Promise<any> {
        return this.setupStripe(clientSecret).then(() => {
            return new Promise((resolve, reject) => {
                this.paymentResolver = resolve;
                this.paymentRejector = reject;
                this.show();
            });
        });
    }

}